import Button, { ActionButton } from '@bugbug/core/components/Button';
import Input from '@bugbug/core/components/Input';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { Header, Footer } from '~/components/modals/Modal';
import useModal from '~/hooks/useModal';
import { useAppSelector } from '~/modules/store';
import { useDuplicateTest } from '~/modules/tests/hooks/useDuplicateTest/useDuplicateTest';
import { selectCurrentTest, selectTest, selectTests } from '~/modules/tests/tests.selectors';
import { VALIDATION_MESSAGE } from '~/utils/validators';

import * as S from './CloneTestModal.styled';

interface CloneTestModalProps {
  id: string;
  shouldNavigate?: boolean;
}
const CloneTestModal = ({ id, shouldNavigate }: CloneTestModalProps) => {
  const { t } = useTranslation();
  const modal = useModal();
  const [duplicateTest, duplicateTestState] = useDuplicateTest();

  const currentTest = useAppSelector(selectCurrentTest);
  const test = useAppSelector(selectTest(id));
  const tests = useAppSelector(selectTests);
  const takenNames = [...tests.map(({ name }) => name), ...(currentTest ? [currentTest.name] : [])];
  const takenNameError = t('cloneTestModal.name.error', 'This name is taken');
  const name = currentTest?.id === id ? currentTest.name : (test?.name ?? '');

  const formik = useFormik<{ name: string }>({
    initialValues: {
      name,
    },
    validationSchema: Yup.object().shape({
      name: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGE.REQUIRED)
        .max(100, VALIDATION_MESSAGE.MAX_LENGTH)
        .notOneOf(takenNames, takenNameError),
    }),
    onSubmit: async (values) => {
      await duplicateTest({
        id,
        shouldNavigate,
        data: { name: values.name.trim() },
      });

      if (!shouldNavigate) {
        modal.hide();
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <Header>{t('cloneTestModal.title', 'Duplicate test?')}</Header>
      <S.FormField label={t('cloneTestModal.name.label', 'Name of duplicated test')} labelId="name">
        <Input
          {...formik.getFieldProps('name')}
          {...formik.getFieldMeta('name')}
          aria-labelledby="name"
          error={formik.touched.name && formik.errors.name}
          autoFocus
        />
      </S.FormField>
      <Footer>
        <Button onClick={modal.hide} variant="primary" bordered>
          {t('default.button.cancel')}
        </Button>
        <ActionButton type="submit" variant="primary" pending={duplicateTestState.isLoading}>
          {t('cloneTestModal.duplicate', 'Duplicate')}
        </ActionButton>
      </Footer>
    </form>
  );
};

export default CloneTestModal;
